import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, Mail } from 'lucide-react';

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif-elegant text-3xl mb-4 text-[#2f2a24]">{title}</h2>
    <div className="space-y-4 text-[#4a4640] leading-relaxed">{children}</div>
  </section>
);

const TermsOfService = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="terms-of-service-page">
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <ScrollText className="w-4 h-4" /> The fine print
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-6xl mb-3">Terms of Service</h1>
          <p className="text-[#6f6a5f]">Last updated: February 1, 2026</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 space-y-10">
        <p className="text-[#4a4640] leading-relaxed">
          Welcome to Herbs We Love. By visiting our website, browsing our botanicals or placing an
          order, you agree to these Terms of Service (the &ldquo;Terms&rdquo;). Please read them carefully.
          If you do not agree with any part of these Terms, please do not use our website or
          services.
        </p>

        <Section title="1. Who We Are">
          <p>
            Herbs We Love is a small-batch herbal and CBD wellness studio based in Eureka,
            California. All products are handcrafted with ethically sourced botanicals and shipped
            from our apothecary. When we say &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our,&rdquo; we mean Herbs We Love.
            &ldquo;You&rdquo; means the person visiting the site or placing an order.
          </p>
        </Section>

        <Section title="2. Not Medical Advice">
          <p>
            The information on this website, in our product descriptions, recipes, rituals, herb
            glossary and emails is provided for educational and general wellness purposes only. It
            is <strong>not medical advice</strong> and is not intended to diagnose, treat, cure or
            prevent any disease or medical condition.
          </p>
          <p>
            Statements about our products have not been evaluated by the U.S. Food &amp; Drug
            Administration. Always consult a qualified healthcare provider before starting any new
            herbal, CBD, or supplement routine &mdash; especially if you are pregnant, nursing,
            taking medication, or managing a health condition.
          </p>
        </Section>

        <Section title="3. Age Requirement">
          <p>
            You must be at least 18 years old (or the legal age of majority in your state) to
            purchase CBD or herbal products from us. By placing an order, you confirm that you meet
            this requirement.
          </p>
        </Section>

        <Section title="4. Orders, Pricing & Payment">
          <p>
            All prices are shown in US dollars and are calculated at checkout. Applicable sales tax
            is calculated based on your shipping address at the time of checkout. We reserve the
            right to correct pricing errors, refuse or cancel any order (including after an order
            has been confirmed), and to limit quantities. Payment is processed securely by our
            payment provider &mdash; we never store your full card details.
          </p>
        </Section>

        <Section title="5. Shipping & Delivery">
          <p>
            We ship within the United States. Please review our{' '}
            <Link to="/policies/shipping" className="underline underline-offset-4">Shipping Policy</Link>{' '}
            for delivery times, carriers, and handling times. Risk of loss passes to you once the
            carrier accepts the package.
          </p>
        </Section>

        <Section title="6. Returns & Refunds">
          <p>
            Because our products are handcrafted, perishable and consumable, we handle returns
            individually with care. Please review our{' '}
            <Link to="/policies/refund" className="underline underline-offset-4">Refund Policy</Link>{' '}
            or reach out to us within 14 days of delivery if something isn&apos;t right.
          </p>
        </Section>

        <Section title="7. Product Use & Allergies">
          <p>
            Our products contain botanicals, essential oils and CBD. Please read every ingredient
            list carefully before use. Discontinue immediately and consult a healthcare provider
            if you experience any adverse reaction. Patch-test topicals on a small area of skin
            before broader use. Keep all products out of reach of children and pets.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            All content on this site &mdash; including photography, product names, recipes, ritual
            cards, herb glossary entries, illustrations, and written copy &mdash; is owned by
            Herbs We Love or its licensors and is protected by copyright and trademark law. You may
            not reproduce, redistribute, or use it commercially without our written permission.
          </p>
        </Section>

        <Section title="9. User Content">
          <p>
            When you submit a product review, testimonial, photo or message, you grant Herbs We
            Love a non-exclusive, royalty-free, worldwide license to use, display and share that
            content on our website, in emails, and on social channels. You represent that the
            content is yours to share and that it doesn&apos;t violate anyone else&apos;s rights.
          </p>
        </Section>

        <Section title="10. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="pl-6 space-y-2 list-disc">
            <li>Use the site for any unlawful purpose or in violation of these Terms.</li>
            <li>Attempt to gain unauthorized access to any part of the site or our systems.</li>
            <li>Interfere with the site&apos;s operation or introduce malware.</li>
            <li>Scrape, resell, or misuse our content or product data.</li>
          </ul>
        </Section>

        <Section title="11. Third-Party Links & Services">
          <p>
            The site may link to third parties (payment processors, shipping carriers, social
            media). We are not responsible for their content, policies or actions. Their use is
            governed by their own terms.
          </p>
        </Section>

        <Section title="12. Disclaimer of Warranties">
          <p>
            Our services and products are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the fullest
            extent permitted by law, we disclaim all warranties, express or implied, including
            merchantability, fitness for a particular purpose, and non-infringement. We do not
            warrant that the site will be uninterrupted, error-free, or free from harmful
            components.
          </p>
        </Section>

        <Section title="13. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, Herbs We Love and its owners, employees and
            partners will not be liable for any indirect, incidental, special, consequential or
            punitive damages arising out of or related to your use of the site or products. Our
            total liability for any claim will not exceed the amount you paid for the product that
            is the subject of the claim.
          </p>
        </Section>

        <Section title="14. Indemnification">
          <p>
            You agree to defend, indemnify and hold harmless Herbs We Love and its team from any
            claim, loss, liability, expense or fee (including reasonable attorneys&apos; fees) arising
            out of your misuse of the site, violation of these Terms, or violation of any
            third-party right.
          </p>
        </Section>

        <Section title="15. Governing Law & Dispute Resolution">
          <p>
            These Terms are governed by the laws of the State of California, without regard to its
            conflict of law rules. Any dispute arising out of or relating to these Terms or your
            use of the site will be resolved by final, binding arbitration administered by the
            American Arbitration Association under its Consumer Arbitration Rules, held in
            Humboldt County, California, or another mutually agreed location. You waive the right
            to a jury trial and to participate in a class action. Small-claims court remains
            available for qualifying claims.
          </p>
        </Section>

        <Section title="16. Changes to These Terms">
          <p>
            We may update these Terms from time to time as our business, laws or best practices
            evolve. Updated Terms will be posted on this page with a new &ldquo;Last updated&rdquo; date.
            Continued use of the site after we post changes means you accept the updated Terms.
          </p>
        </Section>

        <Section title="17. Contact Us">
          <p className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Questions about these Terms? Email us at{' '}
            <a
              className="underline underline-offset-4"
              href="mailto:connectwithanherbalist@outlook.com"
            >
              connectwithanherbalist@outlook.com
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
};

export default TermsOfService;
